import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api-client';
import { useToast, getErrorMessage } from '../hooks/use-toast';
import { ToastContainer } from '../components/toast';

interface Member {
  id: string;
  firstName: string;
  lastName: string;
  treatyNumber: string;
  email: string;
  createdAt: string;
}

export function RegistrationsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [photoPreview, setPhotoPreview] = useState<{ memberId: string; url: string } | null>(null);
  const [reviewModal, setReviewModal] = useState<Member | null>(null);
  const [reviewedIds, setReviewedIds] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();
  const { toasts, showToast, removeToast } = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ['pending-registrations', page],
    queryFn: () => adminApi.getPendingRegistrations(page, 20),
  });

  const approveMutation = useMutation({
    mutationFn: adminApi.approveRegistration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-registrations'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      setReviewModal(null);
      showToast('Member approved successfully', 'success');
    },
    onError: (error) => {
      showToast(getErrorMessage(error), 'error');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ memberId, reason }: { memberId: string; reason?: string }) =>
      adminApi.rejectRegistration(memberId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-registrations'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      setReviewModal(null);
      showToast('Registration rejected', 'info');
    },
    onError: (error) => {
      showToast(getErrorMessage(error), 'error');
    },
  });

  async function viewPhoto(memberId: string) {
    try {
      const data = await adminApi.getMemberPhotoIdUrl(memberId);
      setPhotoPreview({ memberId, url: data.url });
      setReviewedIds((prev) => new Set(prev).add(memberId));
    } catch (error) {
      showToast(getErrorMessage(error), 'error');
    }
  }

  const handleApprove = (memberId: string) => {
    if (!reviewedIds.has(memberId)) {
      showToast('Please view the photo ID first before approving', 'error');
      return;
    }
    if (confirm('I have reviewed the photo ID and confirm this registration should be approved.')) {
      approveMutation.mutate(memberId);
    }
  };

  const handleReject = (memberId: string, reason?: string) => {
    if (reason !== undefined) {
      rejectMutation.mutate({ memberId, reason: reason || undefined });
    }
  };

  const filteredMembers =
    data?.data.filter((member: Member) => {
      if (!search) return true;
      const searchLower = search.toLowerCase();
      return (
        `${member.firstName} ${member.lastName}`.toLowerCase().includes(searchLower) ||
        member.treatyNumber.toLowerCase().includes(searchLower) ||
        member.email.toLowerCase().includes(searchLower)
      );
    }) || [];

  const totalFiltered = filteredMembers.length;
  const startIndex = (page - 1) * 8;
  const paginatedMembers = filteredMembers.slice(startIndex, startIndex + 8);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#4b607f]/20 border-t-[#4b607f] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-[1100px]">
      {/* Photo ID Preview Modal */}
      {photoPreview && (
        <div
          role="dialog"
          aria-label="Photo ID review"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setPhotoPreview(null)}
        >
          <div
            className="max-w-4xl w-full bg-white rounded-[13px] shadow-[0_21px_45px_rgba(0,0,0,0.15)] p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6">
              <h3 className="text-[21px] font-semibold text-[#424a47] mb-2">Photo ID Review</h3>
              <p className="text-[13px] text-[#68716d]">
                Verify identity against the membership record before approving registration
              </p>
            </div>
            <img
              src={photoPreview.url}
              alt="Registration photo ID"
              className="w-full max-h-[70vh] object-contain rounded-[9px] bg-[#f6f6f3] mb-6"
              onLoad={() => setReviewedIds((ids) => new Set(ids).add(photoPreview.memberId))}
            />
            <button
              onClick={() => setPhotoPreview(null)}
              className="w-full bg-[#242728] text-white py-[11px] rounded-[9px] text-[13px] font-semibold hover:bg-[#3c413d] active:scale-[0.98] transition-all"
            >
              Close photo review
            </button>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {reviewModal && (
        <div
          role="dialog"
          aria-label="Review registration"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() =>
            !approveMutation.isPending && !rejectMutation.isPending && setReviewModal(null)
          }
        >
          <div
            className="max-w-[520px] w-full bg-white rounded-[13px] shadow-[0_21px_45px_rgba(0,0,0,0.15)] p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-[21px] font-semibold text-[#424a47] mb-3">Review registration</h3>
            <p className="text-[13px] text-[#68716d] mb-6">
              Check the member's details before making a decision.
            </p>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-[42px] h-[42px] rounded-full bg-[#e1e5eb] text-[#4b607f] flex items-center justify-center font-medium text-[13px]">
                {reviewModal.firstName[0]}
                {reviewModal.lastName[0]}
              </div>
              <div className="flex-1">
                <div className="font-medium text-[#424a47] text-[14px]">
                  {reviewModal.firstName} {reviewModal.lastName}
                </div>
                <div className="text-[12px] text-[#68716d]">{reviewModal.email}</div>
              </div>
              <span className="px-[11px] py-[5px] rounded-full text-[11px] font-medium bg-[#fef8e6] text-[#b8942c] border border-[#f4e6b8]">
                Pending
              </span>
            </div>

            <dl className="space-y-4 mb-6 pb-6 border-b border-[#e5e4df]">
              <div>
                <dt className="text-[11px] text-[#68716d] uppercase tracking-[0.5px] mb-1">
                  Treaty number
                </dt>
                <dd className="text-[14px] text-[#424a47] font-medium">
                  {reviewModal.treatyNumber}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] text-[#68716d] uppercase tracking-[0.5px] mb-1">
                  Registered
                </dt>
                <dd className="text-[14px] text-[#424a47]">
                  {new Date(reviewModal.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </dd>
              </div>
            </dl>

            <div className="bg-[#f1eee7] border border-[#e9e4da] rounded-[9px] p-4 mb-6 flex gap-3">
              <svg
                className="w-[18px] h-[18px] flex-shrink-0 text-[#4b607f]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.65}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
              <p className="text-[12px] text-[#827769] leading-[1.6]">
                Photo IDs must be verified against the membership record. Review identity documents
                before approving.
              </p>
            </div>

            <button
              onClick={() => viewPhoto(reviewModal.id)}
              className={`w-full mb-4 px-4 py-[10px] border rounded-[9px] transition-colors text-[13px] font-medium flex items-center justify-center gap-2 ${
                reviewedIds.has(reviewModal.id)
                  ? 'bg-[#e1e5eb] border-[#d1d8e3] text-[#4b607f]'
                  : 'border-[#e6e9de] hover:bg-[#f6f6f3] text-[#424a47]'
              }`}
            >
              {reviewedIds.has(reviewModal.id) ? (
                <svg
                  className="w-[16px] h-[16px]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg
                  className="w-[16px] h-[16px]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.65}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              )}
              {reviewedIds.has(reviewModal.id) ? 'Photo ID reviewed' : 'View photo ID'}
            </button>

            {!reviewedIds.has(reviewModal.id) && (
              <p className="text-[11px] text-[#68716d] mb-4 text-center italic">
                You must view the photo ID before approving this registration
              </p>
            )}

            <div className="flex gap-3 pt-4 border-t border-[#e5e4df]">
              <button
                onClick={() => {
                  const reason = prompt('Reason for rejection (optional):');
                  if (reason !== null) {
                    handleReject(reviewModal.id, reason);
                  }
                }}
                disabled={rejectMutation.isPending || approveMutation.isPending}
                className="flex-1 px-4 py-[10px] border border-[#e6e9de] rounded-[9px] hover:bg-[#f6f6f3] transition-colors text-[13px] font-medium text-[#424a47] disabled:opacity-50"
              >
                Reject
              </button>
              <button
                onClick={() => handleApprove(reviewModal.id)}
                disabled={
                  approveMutation.isPending ||
                  rejectMutation.isPending ||
                  !reviewedIds.has(reviewModal.id)
                }
                className="flex-1 px-4 py-[10px] bg-[#4b607f] text-white rounded-[9px] text-[13px] font-semibold hover:bg-[#3c4d66] active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <svg
                  className="w-[16px] h-[16px]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.65}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Approve member
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between mb-4">
          <div>
            <span className="block text-[10px] tracking-[1.2px] text-[#68716d] mb-[9px] uppercase font-semibold">
              Opaskwayak Cree Nation
            </span>
            <h1 className="text-[32px] font-semibold text-[#424a47] leading-[1.2] tracking-[-0.5px] mb-[9px]">
              Registration requests
            </h1>
            <p className="text-[14px] text-[#68716d] leading-[1.6]">
              Review new registrations and welcome members to the portal.
            </p>
          </div>
          <span className="px-[13px] py-[7px] rounded-full text-[12px] font-medium bg-[#e1e5eb] text-[#4b607f] border border-[#d1d8e3] whitespace-nowrap">
            {data?.data.length || 0} pending
          </span>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[280px] relative">
          <svg
            className="absolute left-[13px] top-1/2 -translate-y-1/2 w-[16px] h-[16px] text-[#68716d] pointer-events-none"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.65}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search name, email or treaty number"
            className="w-full pl-[41px] pr-4 py-[10px] text-[13px] border border-[#e1e6d8] rounded-[9px] bg-white text-[#424a47] placeholder:text-[#68716d] focus:outline focus:outline-2 focus:outline-[#aeb6bf] focus:outline-offset-2"
          />
        </div>
        {search && (
          <button
            onClick={() => {
              setSearch('');
              setPage(1);
            }}
            className="px-4 py-[10px] border border-[#e6e9de] rounded-[9px] hover:bg-[#f6f6f3] transition-colors text-[13px] font-medium text-[#424a47]"
          >
            Reset filters
          </button>
        )}
      </div>

      {/* Table or Empty State */}
      {data?.data.length === 0 ? (
        <div className="bg-white rounded-[13px] shadow-[0_2px_8px_rgba(0,0,0,0.06)] p-12 text-center">
          <div className="w-[64px] h-[64px] mx-auto mb-4 rounded-full bg-[#f6f6f3] flex items-center justify-center">
            <svg
              className="w-[32px] h-[32px] text-[#6e825a]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.65}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-[18px] font-semibold text-[#424a47] mb-2">All caught up!</h3>
          <p className="text-[14px] text-[#68716d]">
            No pending registrations to review at the moment.
          </p>
        </div>
      ) : paginatedMembers.length === 0 ? (
        <div className="bg-white rounded-[13px] shadow-[0_2px_8px_rgba(0,0,0,0.06)] p-12 text-center">
          <div className="w-[64px] h-[64px] mx-auto mb-4 rounded-full bg-[#f6f6f3] flex items-center justify-center">
            <svg
              className="w-[32px] h-[32px] text-[#68716d]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.65}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <h3 className="text-[18px] font-semibold text-[#424a47] mb-2">No requests found</h3>
          <p className="text-[14px] text-[#68716d] mb-4">
            Try another search or clear your filters.
          </p>
          <button
            onClick={() => {
              setSearch('');
              setPage(1);
            }}
            className="px-4 py-[10px] bg-[#242728] text-white rounded-[9px] text-[13px] font-semibold hover:bg-[#3c413d] active:scale-[0.98] transition-all"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-[13px] shadow-[0_2px_8px_rgba(0,0,0,0.06)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#f6f6f3] border-b border-[#e5e4df]">
                  <tr>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[#68716d] uppercase tracking-[0.5px]">
                      Member
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[#68716d] uppercase tracking-[0.5px]">
                      Treaty number
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[#68716d] uppercase tracking-[0.5px]">
                      Registered
                    </th>
                    <th className="px-4 py-3 text-right text-[11px] font-medium text-[#68716d] uppercase tracking-[0.5px]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e5e4df]">
                  {paginatedMembers.map((member: Member) => (
                    <tr key={member.id} className="hover:bg-[#f6f6f3] transition-colors">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-[36px] h-[36px] rounded-full bg-[#e1e5eb] text-[#4b607f] flex items-center justify-center font-medium text-[12px] flex-shrink-0">
                            {member.firstName[0]}
                            {member.lastName[0]}
                          </div>
                          <div>
                            <div className="font-medium text-[#424a47] text-[14px]">
                              {member.firstName} {member.lastName}
                            </div>
                            <div className="text-[12px] text-[#68716d]">{member.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-[13px] text-[#68716d] font-mono">
                        {member.treatyNumber}
                      </td>
                      <td className="px-4 py-4 text-[13px] text-[#68716d]">
                        {new Date(member.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <button
                          onClick={() => setReviewModal(member)}
                          className="px-4 py-[7px] bg-[#4b607f] text-white rounded-[7px] text-[12px] font-semibold hover:bg-[#3c4d66] active:scale-[0.98] transition-all inline-flex items-center gap-2"
                        >
                          Review
                          <svg
                            className="w-[14px] h-[14px]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={1.65}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Footer */}
          <div className="flex items-center justify-between mt-4 px-2">
            <span className="text-[13px] text-[#68716d]">
              {startIndex + 1}–{Math.min(startIndex + 8, totalFiltered)} of {totalFiltered}{' '}
              {totalFiltered === 1 ? 'request' : 'requests'}
            </span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-[9px] border border-[#e6e9de] rounded-[9px] hover:bg-[#f6f6f3] transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-[13px] font-medium text-[#424a47]"
              >
                Previous
              </button>
              <span className="px-4 py-[9px] text-[13px] font-medium text-[#424a47] min-w-[44px] text-center">
                {page}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={startIndex + 8 >= totalFiltered}
                className="px-4 py-[9px] border border-[#e6e9de] rounded-[9px] hover:bg-[#f6f6f3] transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-[13px] font-medium text-[#424a47]"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

export default RegistrationsPage;
